import {
  takeEvery,
  select,
  call,
  put,
  fork,
  all,
  cancel,
} from 'redux-saga/effects';

import { baseTheme } from '@appcues/theme-utilities';
import {
  replaceAccountThemes as replaceThemes,
  replaceAccountTheme as replaceTheme,
  updateAccountThemeInStore as updateThemeInStore,
  updateAccountThemeAndFlush as updateThemeAndFlush,
} from 'actions/account/themes';
import { SYNC_ACCOUNT_WITH_FIREBASE } from 'constants/actionTypes';
import {
  CLONE_ACCOUNT_THEME,
  CREATE_ACCOUNT_THEME,
  DELETE_ACCOUNT_THEME,
  SET_DEFAULT_THEME,
  UPDATE_ACCOUNT_THEME_AND_FLUSH,
} from 'constants/account/themes';
import { addTimingMeasureInstructionsToAction } from 'utils/timing';
import { selectAccountId } from 'reducers/account/meta';
import { selectUserId } from 'reducers/user';
import {
  selectAccountTheme,
  selectAccountThemes,
} from 'reducers/account/themes';
import { firebaseSet, push, remove } from 'sagas/firebase/lib';
import { showToast } from 'actions/toaster';
import { showDelayedToast } from 'sagas/toaster';
import { reportError } from 'helpers/error-reporting';
import { trackEvent } from 'actions/events';
import { navigate } from 'actions/routing';
import { hideModal } from 'actions/currentModal';
import { hitPublishHook } from 'sagas/account/publishing';
import { CLICKED_BUTTON } from 'constants/events';

export function* replaceAccountTheme(res) {
  const { key, data } = res;
  yield put(replaceTheme(key, data));
}

export function* replaceAccountThemes(res) {
  const { data } = res;
  yield put(
    addTimingMeasureInstructionsToAction(
      replaceThemes(data),
      SYNC_ACCOUNT_WITH_FIREBASE
    )
  );
}

export function* updateAccountThemeAndFlush(action) {
  const {
    payload: { themeId, changes },
  } = action;

  const showDelayedToastJob = yield fork(
    showDelayedToast,
    { type: 'standard', message: 'Still saving Theme...' },
    1500
  );

  const now = yield call([Date, Date.now]);
  const accountId = yield select(selectAccountId);
  const userId = yield select(selectUserId);

  // grab a copy of the theme as it currently exists to roll back to
  const rollbackTheme = yield select(selectAccountTheme, themeId);

  try {
    yield put(
      updateThemeInStore(themeId, {
        ...changes,
        updatedAt: now,
        updatedBy: userId,
        newTheme: false,
      })
    );

    const updatedTheme = yield select(selectAccountTheme, themeId);
    yield call(
      firebaseSet,
      `accounts/${accountId}/styles/${themeId}`,
      updatedTheme
    );

    yield call(hitPublishHook);

    yield all([
      cancel(showDelayedToastJob),
      put(showToast({ type: 'success', message: 'Theme saved successfully' })),
    ]);

    try {
      yield put(
        trackEvent(CLICKED_BUTTON, {
          buttonLabel: 'Update theme',
          theme_id: themeId,
        })
      );
    } catch (error) {
      yield call(reportError, error);
    }
  } catch (error) {
    yield all([
      cancel(showDelayedToastJob),
      put(showToast({ type: 'error', message: 'Failed to save theme' })),
    ]);

    yield call(reportError, error);
    yield put(replaceTheme(themeId, rollbackTheme));
  }
}

export function* createAccountTheme() {
  const accountId = yield select(selectAccountId);
  const now = yield call([Date, Date.now]);
  const userId = yield select(selectUserId);
  const newTheme = {
    createdAt: now,
    createdBy: userId,
    newTheme: true,
    ...baseTheme,
    name: 'New Theme',
    isDefault: false,
  };
  try {
    const themeId = yield call(push, {
      [`accounts/${accountId}/styles`]: newTheme,
    });
    yield call(hitPublishHook);

    try {
      yield put(
        trackEvent(CLICKED_BUTTON, {
          buttonLabel: 'Create theme',
          theme_id: themeId,
        })
      );
    } catch (error) {
      yield call(reportError, error);
    }

    yield put(navigate(`/themes/${themeId}/edit`));
  } catch (error) {
    yield put(showToast({ type: 'error', message: 'Failed to create theme' }));
    yield call(reportError, error);
  }
}

export function* cloneAccountTheme(action) {
  const accountId = yield select(selectAccountId);
  const {
    payload: { originalThemeId },
  } = action;
  const originalTheme = yield select(selectAccountTheme, originalThemeId);
  const newTheme = {
    ...originalTheme,
    isDefault: false,
    name: `Clone of ${originalTheme.name}`,
  };
  try {
    const themeId = yield call(push, {
      [`accounts/${accountId}/styles`]: newTheme,
    });
    yield call(hitPublishHook);

    try {
      yield put(
        trackEvent(CLICKED_BUTTON, {
          buttonLabel: 'Clone theme',
          theme_id: themeId,
        })
      );
    } catch (error) {
      yield call(reportError, error);
    }

    yield put(navigate(`/themes/${themeId}/edit`));
    yield put(
      showToast({ type: 'success', message: 'Theme successfully cloned' })
    );
  } catch (error) {
    yield put(showToast({ type: 'error', message: 'Failed to clone theme' }));
    yield call(reportError, error);
  }
}

export function* deleteAccountTheme(action) {
  const accountId = yield select(selectAccountId);
  const {
    payload: { themeId },
  } = action;

  try {
    yield put(navigate('/themes'));
    yield call(hitPublishHook);
    yield all([
      call(remove, [`accounts/${accountId}/styles/${themeId}`]),
      put(hideModal()),
    ]);

    try {
      yield put(
        trackEvent(CLICKED_BUTTON, {
          buttonLabel: 'Delete theme',
          theme_id: themeId,
        })
      );
    } catch (error) {
      yield call(reportError, error);
    }

    yield put(
      showToast({ type: 'success', message: 'Theme successfully deleted' })
    );
  } catch (error) {
    yield put(showToast({ type: 'error', message: 'Failed to delete theme' }));
    yield call(reportError, error);
  }
}

export function* setDefaultTheme(action) {
  const {
    payload: { themeId },
  } = action;
  // select all themes
  const accountThemes = yield select(selectAccountThemes);
  // Find the defaults
  const defaultThemes = Object.values(accountThemes).filter(theme => {
    return theme.isDefault;
  });
  try {
    // set isDefault to false
    yield all(
      defaultThemes.map(theme => {
        return put(
          updateThemeAndFlush(theme.id, {
            isDefault: false,
          })
        );
      })
    );
    // set isDefault to true on this one
    yield put(updateThemeAndFlush(themeId, { isDefault: true }));
    yield call(hitPublishHook);
  } catch (error) {
    yield put(
      showToast({ type: 'error', message: 'Failed to update default theme' })
    );
    yield call(reportError, error);
  }
}

export default function* themeSagas() {
  yield takeEvery(UPDATE_ACCOUNT_THEME_AND_FLUSH, updateAccountThemeAndFlush);
  yield takeEvery(CREATE_ACCOUNT_THEME, createAccountTheme);
  yield takeEvery(CLONE_ACCOUNT_THEME, cloneAccountTheme);
  yield takeEvery(DELETE_ACCOUNT_THEME, deleteAccountTheme);
  yield takeEvery(SET_DEFAULT_THEME, setDefaultTheme);
}
